import React from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTag, faTimes, faExclamationCircle } from '@fortawesome/free-solid-svg-icons';

const PromoCodeInput = ({ 
  promoCode, 
  setPromoCode, 
  onApply, 
  promoError, 
  promoMessage, 
  promoDiscount,
  onRemoveDiscount,
  theme 
}) => {
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      onApply();
    }
  };

  const styles = {
    container: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '12px',
      flexWrap: 'wrap', // Allow wrapping on mobile
    },
    compactWrapper: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      background: theme?.backgrounds?.card,
      border: `1px solid ${theme?.borders?.primary}`,
      borderRadius: '50px',
      padding: '6px 8px 6px 16px',
      boxShadow: theme?.shadows?.sm,
      transition: 'all 0.3s ease',
    },
    compactWrapperHover: {
      borderColor: theme?.primary?.blue?.[500],
      boxShadow: `0 0 0 3px ${theme?.primary?.blue?.[500]}15`,
    },
    icon: {
      color: theme?.text?.secondary,
      fontSize: '0.8rem',
    },
    input: {
      border: 'none',
      background: 'transparent',
      outline: 'none',
      fontSize: '0.85rem',
      width: '100px',
      color: theme?.text?.primary,
      fontWeight: '500',
      textTransform: 'uppercase',
    },
    applyButton: {
      background: theme?.primary?.blue?.[500] || '#3B82F6',
      color: 'white',
      border: 'none',
      borderRadius: '20px',
      padding: '6px 12px',
      fontSize: '0.8rem',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: '60px',
      minHeight: '32px',
    },
    applyButtonHover: {
      background: theme?.primary?.blue?.[600],
      transform: 'scale(1.05)',
    },
    applyButtonDisabled: {
      background: theme?.borders?.primary || '#E5E7EB',
      color: theme?.text?.tertiary || '#9CA3AF',
      cursor: 'not-allowed',
      opacity: 0.6,
    },
    // Success Banner (Discount Applied)
    successBanner: {
      background: `linear-gradient(135deg, ${theme?.primary?.green[500]} 0%, ${theme?.primary?.green[600]} 100%)`,
      color: 'white',
      padding: '6px 16px',
      borderRadius: '50px',
      fontWeight: '600',
      fontSize: '0.85rem',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      boxShadow: theme?.shadows?.sm,
      
    },
    // Error Banner
    errorBanner: {
      background: `linear-gradient(135deg, ${theme?.primary?.red[500]} 0%, ${theme?.primary?.red[600]} 100%)`,
      color: 'white',
      padding: '6px 16px',
      borderRadius: '50px',
      fontWeight: '600',
      fontSize: '0.85rem',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      boxShadow: theme?.shadows?.sm,
    },
    removeButton: {
      background: 'rgba(255, 255, 255, 0.2)',
      border: 'none',
      color: 'white',
      padding: '2px 6px',
      borderRadius: '12px',
      fontSize: '0.7rem',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      display: 'flex',
      alignItems: 'center',
      gap: '3px',
    },
    removeButtonHover: {
      background: 'rgba(255, 255, 255, 0.3)',
    },
  };

  return (
    <motion.div 
      style={styles.container}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.2 }}
    >
      {/* Compact Promo Input */}
      <motion.div
        style={styles.compactWrapper}
        whileHover={styles.compactWrapperHover}
      >
        <FontAwesomeIcon icon={faTag} style={styles.icon} />
        <input
          type="text"
          placeholder="Promo code"
          value={promoCode}
          onChange={(e) => setPromoCode(e.target.value)}
          onKeyDown={handleKeyDown}
          style={styles.input}
          maxLength={20}
        />
        <motion.button
          style={styles.applyButton}
          onClick={onApply}
          whileTap={{ scale: 0.95 }}
        >
          Apply
        </motion.button>
        
        
        
      </motion.div>
      {promoDiscount > 0 && (
        <motion.div 
          style={styles.successBanner}
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
        >
          <span>🎉 {promoDiscount}% OFF applied!</span>
          <motion.button
            style={styles.removeButton}
            whileHover={styles.removeButtonHover}
            whileTap={{ scale: 0.95 }}
            onClick={onRemoveDiscount}
          >
            <FontAwesomeIcon icon={faTimes} />
          </motion.button>
        </motion.div>
      )}

      {/* Error Banner - Show when there's an error */}
      {promoError && (
        <motion.div 
          style={styles.errorBanner}
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
        >
          <FontAwesomeIcon icon={faExclamationCircle} />
          <span>{promoError}</span>
        </motion.div>
      )}
    </motion.div>
  );
};

export default PromoCodeInput;